/**
 * Predictive Search — StratOS Stadium Premium Client Edition
 * Fetches /search/suggest.json on keyup (debounced) and renders a dropdown.
 */
(function () {
  'use strict';

  if (customElements.get('predictive-search')) return;

  var DEBOUNCE_MS   = 250;
  var MIN_CHARS     = 2;
  var PRODUCT_LIMIT = 5;
  var COLL_LIMIT    = 3;

  function formatMoney(cents) {
    if (window.ShopUtils && window.ShopUtils.formatMoney) {
      return window.ShopUtils.formatMoney(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function escape(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function highlight(text, query) {
    if (!query || !text) return escape(text);
    var safeText  = escape(String(text));
    var safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safeText.replace(
      new RegExp('(' + escape(safeQuery) + ')', 'gi'),
      '<mark>$1</mark>'
    );
  }

  function imgUrl(src, width) {
    if (!src) return '';
    return src.replace(/(\.[^.?]+)(\?.*)?$/, '_' + width + 'x$1$2');
  }

  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this._input      = this.querySelector('input[type="search"], input[name="q"]');
      /* Results container may live outside this element (e.g. as sibling in expanding-search) */
      var expSearch    = this.closest('expanding-search');
      this._results    = (expSearch || this).querySelector('[data-predictive-results]');
      this._query      = '';
      this._timer      = null;
      this._activeIdx  = -1;
      this._items      = [];
      this._controller = null;

      if (!this._input || !this._results) return;

      this._input.setAttribute('autocomplete', 'off');

      this._input.addEventListener('input',   this._onInput.bind(this));
      this._input.addEventListener('keydown', this._onKeydown.bind(this));
      this._input.addEventListener('focus',   this._onFocus.bind(this));

      document.addEventListener('click', this._onOutside.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onOutside.bind(this));
    }

    _onInput() {
      clearTimeout(this._timer);
      var q = this._input.value.trim();
      this._query = q;

      if (q.length < MIN_CHARS) {
        this._hide();
        return;
      }

      this._showLoading();
      this._timer = setTimeout(this._fetch.bind(this, q), DEBOUNCE_MS);
    }

    _onFocus() {
      if (this._query && this._query.length >= MIN_CHARS && this._items.length > 0) {
        this._results.removeAttribute('hidden');
      }
    }

    _onOutside(e) {
      /* Results container may be outside <predictive-search> (e.g. sibling in expanding-search) */
      var container = this.closest('expanding-search') || this;
      if (!container.contains(e.target)) this._hide();
    }

    _onKeydown(e) {
      if (this._results.hasAttribute('hidden')) return;

      var links = Array.from(this._results.querySelectorAll('.predictive-search__item'));
      if (!links.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this._activeIdx = Math.min(this._activeIdx + 1, links.length - 1);
        this._updateActive(links);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this._activeIdx = Math.max(this._activeIdx - 1, -1);
        this._updateActive(links);
      } else if (e.key === 'Enter' && this._activeIdx >= 0) {
        e.preventDefault();
        links[this._activeIdx].click();
      } else if (e.key === 'Escape') {
        this._hide();
        this._input.blur();
      }
    }

    _updateActive(links) {
      links.forEach(function (l, i) {
        if (i === this._activeIdx) {
          l.classList.add('is-active');
          l.scrollIntoView({ block: 'nearest' });
        } else {
          l.classList.remove('is-active');
        }
      }, this);
    }

    _fetch(q) {
      if (this._controller) this._controller.abort();
      this._controller = new AbortController();

      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection'
        + '&resources[limit]=' + PRODUCT_LIMIT
        + '&resources[options][unavailable_products]=last';

      fetch(url, { signal: this._controller.signal })
        .then(function (r) { return r.json(); })
        .then(this._render.bind(this, q))
        .catch(function (err) {
          if (err.name !== 'AbortError') console.warn('[predictive-search]', err);
        });
    }

    _render(q, data) {
      var resources = (data.resources && data.resources.results) || {};
      var products  = resources.products  || [];
      var colls     = resources.collections || [];

      this._items = [];
      var html    = '';

      var i18n = window.predictiveSearchI18n || {};

      /* Products */
      if (products.length) {
        html += '<p class="predictive-search__heading">' + escape(i18n.products || 'Produits') + '</p>';
        products.slice(0, PRODUCT_LIMIT).forEach(function (p) {
          this._items.push(p);
          var img    = p.featured_image ? imgUrl(p.featured_image.url || p.featured_image, 80) : '';
          var price  = p.price_min      ? formatMoney(p.price_min) : '';
          html += '<a href="' + p.url + '" class="predictive-search__item">';
          if (img) {
            html += '<img src="' + escape(img) + '" alt="' + escape(p.title) + '" width="44" height="58" class="predictive-search__item-image" loading="lazy">';
          }
          html += '<div class="predictive-search__item-info">'
            + '<p class="predictive-search__item-title">' + highlight(p.title, q) + '</p>'
            + (price ? '<p class="predictive-search__item-price">' + price + '</p>' : '')
            + '</div></a>';
        }, this);
      }

      /* Collections */
      if (colls.length) {
        if (products.length) html += '<hr class="predictive-search__divider">';
        html += '<p class="predictive-search__heading">' + escape(i18n.collections || 'Collections') + '</p>';
        colls.slice(0, COLL_LIMIT).forEach(function (c) {
          html += '<a href="' + c.url + '" class="predictive-search__item predictive-search__item--collection">'
            + '<div class="predictive-search__item-info"><p class="predictive-search__item-title">' + highlight(c.title, q) + '</p></div>'
            + '</a>';
        });
      }

      /* Empty state */
      if (!products.length && !colls.length) {
        var noResults = (i18n.no_results || 'Aucun résultat pour « __q__ »').replace('__q__', escape(q));
        html += '<div class="predictive-search__empty-state">'
          + '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>'
          + '<p class="predictive-search__empty">' + noResults + '</p>'
          + '<a href="/search?q=' + encodeURIComponent(q) + '" class="predictive-search__all-link">'
          + (i18n.see_all || 'Voir tous les résultats')
          + '</a>'
          + '</div>';
      }

      /* Footer — all results link */
      html += '<div class="predictive-search__footer">'
        + '<a href="/search?q=' + encodeURIComponent(q) + '&type=product">' + escape(i18n.see_all || 'Voir tous les résultats') + ' →</a>'
        + '</div>';

      this._results.innerHTML = html;
      this._results.removeAttribute('hidden');
      this._activeIdx = -1;
    }

    _showLoading() {
      this._results.innerHTML = '<p class="predictive-search__loading">Recherche…</p>';
      this._results.removeAttribute('hidden');
    }

    _hide() {
      this._results.setAttribute('hidden', '');
      this._activeIdx = -1;
    }
  });
})();
